import * as ScrollArea from '@radix-ui/react-scroll-area';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../button/button';
import { Inline } from '../inline/inline';

type ScrollDirection = 'left' | 'right';

type HorizontalScrollAreaProps = {
  children: ReactNode;
  className?: string;
};

export const HorizontalScrollArea = ({ children, className }: HorizontalScrollAreaProps) => {
  const [showLeftScrollButton, setShowLeftScrollButton] = useState(false);
  const [showRightScrollButton, setShowRightScrollButton] = useState(false);
  const scrollAreaViewportRef = useRef<HTMLDivElement>(null);

  const checkOverflow = useCallback(() => {
    if (!scrollAreaViewportRef.current) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scrollAreaViewportRef.current;

    setShowLeftScrollButton(scrollLeft > 0);
    setShowRightScrollButton(scrollLeft + clientWidth < scrollWidth);
  }, []);

  const scrollViewport = (direction: ScrollDirection) => {
    if (!scrollAreaViewportRef.current) {
      return;
    }

    const scrollAmount = scrollAreaViewportRef.current.clientWidth * 0.8;

    scrollAreaViewportRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  // useLayoutEffect runs the code as part of the same task with the rest of the component, thus removing a UI flicker effect
  // Needed here to avoid a UI flicker effect when the user may change the viewport's width (i.e. filtering cards)
  // Docs say it can hurt performance but it is recommended for cases like this, to avoid UI flicker effects
  // https://react.dev/reference/react/useLayoutEffect
  // useEffect runs the code in a separate task
  useLayoutEffect(() => {
    const viewport = scrollAreaViewportRef.current;

    if (!viewport) {
      return;
    }

    // Observes changes to the viewport's dimensions and triggers the checkOverflow function
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(viewport);

    viewport.addEventListener('scroll', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      viewport.removeEventListener('scroll', checkOverflow);
    };
  }, [checkOverflow]);

  return (
    <ScrollArea.Root className={cn('relative w-full min-w-0 flex-1', className)}>
      <ScrollArea.Viewport ref={scrollAreaViewportRef}>
        <Inline wrap="nowrap" align="stretch" space={3}>
          {children}
        </Inline>
      </ScrollArea.Viewport>

      <ScrollArea.Scrollbar orientation="horizontal">
        <ScrollArea.Thumb />
      </ScrollArea.Scrollbar>

      {showLeftScrollButton && (
        <>
          {/* Gradient left layer */}
          {/* z-20 needed so the gradient applies to the absolute positioned demo account label */}
          <div className="pointer-events-none absolute inset-x-0 inset-y-0 z-20 bg-gradient-to-r from-white to-transparent to-5%" />

          <ScrollIconWrapper direction="left" scrollViewport={() => scrollViewport('left')}>
            <ChevronLeft className="h-full w-full" />
          </ScrollIconWrapper>
        </>
      )}

      {showRightScrollButton && (
        <>
          {/* Gradient right layer */}
          {/* z-20 needed so the gradient applies to the absolute positioned demo account label */}
          <div className="pointer-events-none absolute inset-x-0 inset-y-0 z-10 bg-gradient-to-r from-transparent from-95% to-white" />

          <ScrollIconWrapper direction="right" scrollViewport={() => scrollViewport('right')}>
            <ChevronRight className="h-full w-full" />
          </ScrollIconWrapper>
        </>
      )}
    </ScrollArea.Root>
  );
};

type ScrollIconWrapperProps = {
  direction: ScrollDirection;
  scrollViewport: () => void;
  children: ReactNode;
};

const ScrollIconWrapper = ({ direction, scrollViewport, children }: ScrollIconWrapperProps) => {
  return (
    <Button
      variant="ghost"
      size="none"
      // -translate-y-1/2 moves it up by half of the button's height to achieve a perfect centered effect
      className={cn('absolute top-1/2 z-20 -translate-y-1/2', {
        'left-3': direction === 'left',
        'right-3': direction === 'right',
      })}
      onClick={scrollViewport}
    >
      <div className="h-9 w-9 overflow-clip rounded-full bg-primary p-2">{children}</div>
    </Button>
  );
};
